const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface ApiError {
  error: string
  message: string
  status: number
}

export interface SearchParams {
  q?: string
  capability?: string
  trust_tier?: string
  tag?: string
  cursor?: string
  limit?: number
}

export interface PackageSummary {
  package_id: string
  slug: string
  name: string
  summary?: string
  publisher_id: string
  publisher_name?: string
  capabilities: string[]
  tags: string[]
  trust_tier: 'unreviewed' | 'approved'
  badges: string[]
  open_source: boolean
  license?: string
  latest_version?: string
  connection_count?: number
  view_count?: number
  icon_url?: string
}

export interface SearchResponse {
  items: PackageSummary[]
  next_cursor?: string
}

export interface PackageDetail extends PackageSummary {
  description?: string
  repo_url?: string
  docs_url?: string
  license?: string
  server_url?: string
  health_status?: string
  health_checked_at?: string
  versions?: VersionSummary[]
  endpoints?: Endpoint[]
}

export interface VersionSummary {
  version: string
  published_at: string
  review_status: string
  toolset_hash?: string
}

export interface Endpoint {
  env: string
  transport: string
  base_url: string
  auth_mode: string
  health_status?: string
}

export interface ToolDescriptor {
  tool_name: string
  title: string
  description?: string
  input_schema: Record<string, unknown>
  output_schema?: Record<string, unknown>
  capabilities?: string[]
}

export interface Publisher {
  publisher_id: string
  slug: string
  display_name: string
  verified: boolean
  website_url?: string
  created_at: string
  package_count?: number
}

export interface Review {
  package_version_id: string
  package_id: string
  package_name: string
  version: string
  review_status: string
  submitted_at?: string
  publisher_name?: string
  notes?: string
}

export interface AdminUser {
  user_id: string
  email: string
  role: string
  created_at: string
  last_login?: string
}

export interface CollectionItem {
  package_id: string
  version_constraint: string
  tool_allowlist?: string[]
  package_name?: string
  package_slug?: string
  package_summary?: string
  open_source?: boolean
}

export interface Collection {
  collection_id: string
  org_id: string
  owner_id?: string
  owner_name?: string
  name: string
  description?: string
  is_public: boolean
  items: CollectionItem[]
  item_count?: number
  created_at?: string
}

// Logger utility
function logRequest(method: string, url: string, body?: unknown) {
  console.log(`[API] ${method} ${url}`, body ? { body } : '')
}

function logResponse(method: string, url: string, status: number, data?: unknown) {
  console.log(`[API] ${method} ${url} -> ${status}`, data ? { data } : '')
}

function logError(method: string, url: string, error: unknown) {
  console.error(`[API] ${method} ${url} ERROR:`, error)
}

async function apiRequest<T>(
  method: string,
  path: string,
  options: {
    body?: unknown
    token?: string
    params?: Record<string, string | number | undefined>
  } = {}
): Promise<T> {
  const { body, token, params } = options

  let url = `${API_BASE_URL}${path}`
  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value))
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  logRequest(method, url, body)

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  let data: unknown = null
  try {
    const text = await response.text()
    if (text) {
      data = JSON.parse(text)
    } else {
      console.warn('[API] Empty response body')
    }
  } catch (e) {
    console.error('[API] Failed to parse response JSON:', e)
  }

  logResponse(method, url, response.status, data)

  if (!response.ok) {
    const errorData = data as { error?: string; message?: string } | null
    const error: ApiError = {
      error: errorData?.error || 'request_failed',
      message: errorData?.message || `Request failed with status ${response.status}`,
      status: response.status,
    }
    logError(method, url, error)
    throw error
  }

  return data as T
}

// Public API functions
export const api = {
  // Search packages
  searchPackages: (params: SearchParams) =>
    apiRequest<SearchResponse>('GET', '/v1/packages/search', {
      params: {
        q: params.q,
        capability: params.capability,
        trust_tier: params.trust_tier,
        tag: params.tag,
        cursor: params.cursor,
        limit: params.limit,
      },
    }),

  // Get package by ID or slug
  getPackage: (packageId: string) =>
    apiRequest<PackageDetail>('GET', `/v1/packages/${packageId}`),

  // Resolve slug to package
  resolvePackage: (slug: string) =>
    apiRequest<{ package_id: string }>('GET', '/v1/resolve', {
      params: { slug },
    }),

  // Get package versions
  getVersions: (packageId: string) =>
    apiRequest<{ items: VersionSummary[] }>('GET', `/v1/packages/${packageId}/versions`),

  // Get package skills
  getTools: (packageId: string, version: string) =>
    apiRequest<{ items: ToolDescriptor[] }>(
      'GET',
      `/v1/packages/${packageId}/versions/${version}/tools`
    ),

  // Get publisher
  getPublisher: (publisherId: string, token?: string) =>
    apiRequest<Publisher>('GET', `/v1/publishers/${publisherId}`, { token }),

  // Get publisher packages
  getPublisherPackages: (publisherId: string) =>
    apiRequest<SearchResponse>('GET', '/v1/packages/search', {
      params: { publisher_id: publisherId },
    }),

  // Public Collections (no auth required)
  getPublicCollections: () =>
    apiRequest<{ items: Collection[] }>('GET', '/v1/collections/public'),

  getPublicCollection: (collectionId: string) =>
    apiRequest<Collection>('GET', `/v1/collections/public/${collectionId}`),

  // User Collections (auth required)
  getCollections: (token: string) =>
    apiRequest<{ items: Collection[] }>('GET', '/v1/collections', { token }),

  getCollection: (collectionId: string, token: string) =>
    apiRequest<Collection>('GET', `/v1/collections/${collectionId}`, { token }),

  createCollection: (
    token: string,
    data: { org_id: string; name: string; description?: string; is_public?: boolean }
  ) =>
    apiRequest<Collection>('POST', '/v1/collections', { token, body: data }),

  updateCollection: (
    token: string,
    collectionId: string,
    data: { name?: string; description?: string; is_public?: boolean }
  ) =>
    apiRequest<Collection>('PATCH', `/v1/collections/${collectionId}`, { token, body: data }),

  addCollectionItem: (
    token: string,
    collectionId: string,
    item: { package_id: string; version_constraint: string; tool_allowlist?: string[] }
  ) =>
    apiRequest<Collection>('POST', `/v1/collections/${collectionId}/items`, {
      token,
      body: item,
    }),

  exportCollection: (collectionId: string, target: string, token: string) =>
    apiRequest<{ config: Record<string, unknown> }>(
      'GET',
      `/v1/collections/${collectionId}/export`,
      { token, params: { target } }
    ),
}

// Publisher API functions (authenticated publisher endpoints)
export const publisherApi = {
  getProfile: (token: string) =>
    apiRequest<Publisher>('GET', '/v1/publisher/me', { token }),

  updateProfile: (
    token: string,
    data: {
      display_name?: string
      email?: string
      website_url?: string
      bio?: string
      slug?: string
      type?: string
    }
  ) =>
    apiRequest<{ publisher: Publisher }>('PATCH', '/v1/publisher/me', {
      token,
      body: data,
    }),

  changePassword: (
    token: string,
    data: { current_password: string; new_password: string }
  ) =>
    apiRequest<{ success: boolean }>('POST', '/v1/publisher/me/password', {
      token,
      body: data,
    }),

  getPackages: (token: string) =>
    apiRequest<{ items: PackageSummary[] }>('GET', '/v1/publisher/me/packages', {
      token,
    }),

  getCollections: (token: string) =>
    apiRequest<{ items: Collection[] }>('GET', '/v1/publisher/me/collections', {
      token,
    }),

  checkSlug: (token: string, slug: string) =>
    apiRequest<{ available: boolean; slug: string }>(
      'GET',
      '/v1/publisher/slug-check',
      { token, params: { slug } }
    ),

  // Create a package
  createPackage: (
    token: string,
    data: {
      slug: string
      name: string
      summary?: string
      description?: string
      capabilities?: string[]
      tags?: string[]
      server_url?: string
    }
  ) =>
    apiRequest<PackageSummary>('POST', '/v1/packages', { token, body: data }),

  // Health check a package's server
  healthCheck: (token: string, packageId: string) =>
    apiRequest<{ health_status: string; checked_at: string }>(
      'POST',
      `/v1/packages/${packageId}/health-check`,
      { token }
    ),
}

// Admin API functions
export const adminApi = {
  // Login
  login: (email: string, password: string) =>
    apiRequest<{ token: string; user: AdminUser }>('POST', '/v1/admin/login', {
      body: { email, password },
    }),

  // Get reviews pending action
  getReviews: (token: string, status?: string) =>
    apiRequest<{ items: Review[] }>('GET', '/v1/admin/reviews', {
      token,
      params: { status },
    }),

  // Get review details
  getReview: (token: string, packageVersionId: string) =>
    apiRequest<Review>('GET', `/v1/reviews/${packageVersionId}`, { token }),

  // Apply review action
  applyReviewAction: (
    token: string,
    packageVersionId: string,
    action: 'approve' | 'request_changes' | 'suspend',
    notes?: string
  ) =>
    apiRequest<Review>('POST', `/v1/reviews/${packageVersionId}/actions`, {
      token,
      body: { action, notes },
    }),

  // Get publishers
  getPublishers: (token: string) =>
    apiRequest<{ items: Publisher[] }>('GET', '/v1/admin/publishers', { token }),

  // Update publisher
  updatePublisher: (
    token: string,
    publisherId: string,
    updates: { verified?: boolean; suspended?: boolean }
  ) =>
    apiRequest<Publisher>('PATCH', `/v1/admin/publishers/${publisherId}`, {
      token,
      body: updates,
    }),

  // Get users
  getUsers: (token: string) =>
    apiRequest<{ items: AdminUser[] }>('GET', '/v1/admin/users', { token }),

  // Get dashboard stats
  getStats: (token: string) =>
    apiRequest<{
      packages_count: number
      publishers_count: number
      pending_reviews: number
      subscriptions_count: number
    }>('GET', '/v1/admin/stats', { token }),

  // Get waitlist signups
  getWaitlist: (token: string) =>
    apiRequest<{ items: WaitlistEntry[]; count: number }>('GET', '/v1/admin/waitlist', { token }),

  // Beta token management
  getBetaTokens: (token: string) =>
    apiRequest<{ items: BetaToken[]; count: number }>('GET', '/v1/admin/beta-tokens', { token }),

  createBetaToken: (
    token: string,
    data: { label: string; count: number; expires_in_days?: number }
  ) =>
    apiRequest<{ tokens: { token: string; token_id: string; label: string; status: string; expires_at?: string }[] }>(
      'POST',
      '/v1/admin/beta-tokens',
      { token, body: data }
    ),

  updateBetaToken: (token: string, tokenId: string, status: 'active' | 'suspended' | 'revoked') =>
    apiRequest<{ token_id: string; status: string }>('PATCH', `/v1/admin/beta-tokens/${tokenId}`, {
      token,
      body: { status },
    }),

  // User management
  createUser: (
    token: string,
    data: { email: string; password: string; role: 'admin' | 'reviewer' }
  ) =>
    apiRequest<{ user_id: string; email: string; role: string }>(
      'POST',
      '/v1/admin/users',
      { token, body: data }
    ),

  deleteUser: (token: string, userId: string) =>
    apiRequest<void>('DELETE', `/v1/admin/users/${userId}`, { token }),
}

export interface WaitlistEntry {
  email: string
  tool_interests?: string
}

export interface BetaToken {
  token_id: string
  token_prefix: string
  label: string
  publisher_id?: string
  created_by: string
  status: 'active' | 'suspended' | 'revoked'
  last_used_at?: string
  expires_at?: string
  created_at: string
}
